import { useNewCode } from '@calendly/feature-check-react'
import { useSelector } from 'react-redux'

import { useGetUserQuery } from 'common'
import Settings from 'core/settings'
import { addonsSelector, hasAddonsSelector } from 'features/billing'
import { selectAll, useFetchCalendarAccountsQuery } from 'features/calendar_connections'
import { useNotetakerEntitled } from 'features/meeting_intelligence'
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events'
import { currentOrganizationSelector } from 'selectors/current_organization'
import Storage from 'services/base/storage'

import { setChatActiveState, setChatClosedState, setChatInactiveState, setChatOpenState } from './slice'

const LOCAL_STORAGE_PREFIX = 'ada_chat'
const USER_ID_KEY = 'ada_chat_user'
const localStorage = new Storage(LOCAL_STORAGE_PREFIX)

export const useAdaParams = () => {
  const track = useTrack()
  const { isEntitled: isNotetakerEntitled } = useNotetakerEntitled()
  const { data, isLoading } = useFetchCalendarAccountsQuery()
  const accounts = data && selectAll(data)
  const pushCalendarConnectionKind = !!data && Object.values(data.entities || {}).find(({ pushEnabled }) => pushEnabled)?.kind
  const pullCalendarConnectionKinds = Array.isArray(accounts) ? accounts.reduce((acc, obj) => {
    acc.push(obj.kind)
    return acc
  }, []) : []

  const { data: {
    id: currentUserId,
    email: currentUserEmail,
    organization_membership: { role: currentUserRole },
    company_role: customerIcp,
    authentication_name: authType,
    profile: { slug: currentUserSlug },
    timezone: currentUserTimezone,
    created_at: currentUserCreatedAt
  } } = useGetUserQuery()
  const { id: organizationId, stage, tier, created_at: organizationCreatedAt } = useSelector(currentOrganizationSelector)
  const hasAddons = useSelector(hasAddonsSelector)
  const enabledAddons = useSelector(addonsSelector)

  const enabledAddonSkus = enabledAddons.reduce((acc, obj) => {
    acc.push(obj.sku_id)
    return acc
  }, [])

  const addons = hasAddons ? enabledAddonSkus : false

  const userParams = {
    currentUserId,
    currentUserEmail,
    currentUserRole,
    authType,
    currentUserSlug,
    currentUserTimezone,
    customerIcp,
    currentUserCreatedAt
  }

  const organizationParams = {
    organizationId,
    organizationCreatedAt
  }

  const billingParams = {
    stage,
    tier,
    addons
  }

  const calendarConnectionParams = {
    pushCalendarConnectionKind,
    pullCalendarConnectionKinds
  }

  const featureFlags = {
    featureFlagsMeetingIntelligence: [
      ...(isNotetakerEntitled ? ['meeting_intelligence'] : []),
    ],
    featureFlagsCRM: [
      ...useNewCode('crm_gmail_sync_1_feature', false)?.enabled ? ['crm_gmail_sync_1_feature'] : [],
    ],
    featureFlagsCommerce: ['commerce_booking_codes_feature']
  }

  const params = {
    ...userParams,
    ...organizationParams,
    ...billingParams,
    ...calendarConnectionParams,
    ...featureFlags,
    track
  }

  return [
    params,
    isLoading
  ]
}

export const initializeChat = async (params, dispatch, chatButtonEnabled = false) => {
  const {
    currentUserId,
    currentUserEmail,
    currentUserRole,
    stage,
    tier,
    addons,
    organizationId,
    organizationCreatedAt,
    authType,
    pushCalendarConnectionKind,
    pullCalendarConnectionKinds,
    currentUserSlug,
    currentUserTimezone,
    customerIcp,
    currentUserCreatedAt,
    featureFlagsMeetingIntelligence,
    featureFlagsCRM,
    featureFlagsCommerce,
    track
  } = params

  let segmentAnonymousId

  const fireAnalytics = (action) => {
    const isPlanSelectionModal = window.location.search.includes('planSelection=true')
    track({
      name: TRACKING_EVENT_NAMES.ADA_CLICKED,
      entities: ['user'],
      properties: {
        action,
        ...(isPlanSelectionModal ? { location: 'upgrade_plan' } : {}),
        path: window.location.pathname
      }
    })
  }

  if (window.analytics
    && window.analytics.user
    && typeof window.analytics.user === 'function'
    && window.analytics.user().anonymousId
    && typeof window.analytics.user().anonymousId === 'function'
  ) {
    segmentAnonymousId = window.analytics.user().anonymousId()
  } else {
    segmentAnonymousId = ''
  }

  // Settings.ada comes from /api/app/settings; bot_handle is a string (Ada bot identifier, e.g. 'calendly').
  await window.adaEmbed.start({
    handle: Settings.ada?.bot_handle,
    align: chatButtonEnabled ? 'right' : 'left',
    crossWindowPersistence: true,
    metaFields: {
      userid: currentUserId,
      email: currentUserEmail,
      role: currentUserRole,
      organization_plan: tier,
      organization_stage: stage,
      organization_id: organizationId,
      organization_created_at: organizationCreatedAt,
      billing_addons: addons,
      feature_flags_enabled: [
        ...featureFlagsMeetingIntelligence || [],
        ...featureFlagsCRM || [],
        ...featureFlagsCommerce || []
      ],
      user_pull_calendar_kinds: pullCalendarConnectionKinds,
      user_push_calendar_kind: pushCalendarConnectionKind,
      user_auth_type: authType,
      user_slug: currentUserSlug,
      user_timezone: currentUserTimezone,
      user_icp: customerIcp,
      user_created_at: currentUserCreatedAt,
      anonymousid: segmentAnonymousId
    },
    toggleCallback: (isDrawerOpen) => {
      fireAnalytics('engaged')
      if (isDrawerOpen) {
        dispatch(setChatOpenState())
        dispatch(setChatActiveState())
      } else {
        dispatch(setChatClosedState())
      }
    },
    conversationEndCallback: () => {
      fireAnalytics('end_chat')
      dispatch(setChatInactiveState())
    }
  })

  const userId = parseInt(localStorage.get(USER_ID_KEY), 10)
  if (userId !== currentUserId) {
    await window.adaEmbed.deleteHistory()
    localStorage.set(USER_ID_KEY, currentUserId)
  }
}

export const openChat = async () => {
  const { isDrawerOpen } = await window.adaEmbed.getInfo()
  if (!isDrawerOpen) {
    await window.adaEmbed.toggle()
  }
}
